<script setup lang="ts">
import { ReviewsProps } from "./Reviews.props"
import { smOptions, mdOptions } from "./Reviews.carouselOptions"
import { sm } from "~/utils/breakpoints"

defineProps<ReviewsProps>()

const carouselOptions = computed(() => (sm.value ? smOptions : mdOptions))

const activeIndex = ref(0)

const { openModal } = useModal()

const handleReadMore = (index: number) => {
  openModal("review")
  activeIndex.value = index
}

const { feedatyRating, feedatyRatingLabel, feedatyReviewsCount, feedatyReviewsCountLabel, feedatyPageUrl } =
  useFeedatyRating()
</script>

<template>
  <section
    class="
      rails-reviews
      padded
      carousel-container-overflowed
      from-yellow-10
      to-white
      flex flex-col
      items-center
      !bg-default-red-500
      bg-gradient-to-r
      py-4
      md:py-12
    "
  >
    <AppLink :to="feedatyPageUrl" class="mb-6 block md:hidden">
      <UtilsIcon name="Feedaty3.svg" class="h-20 w-20" />
    </AppLink>
    <p
      v-if="title"
      class="
        elephant-bold
        md:mammoth-bold
        text-green-main
        mb-3
        md:mb-4
        md:text-center
        lg:mb-4
      "
    >
      {{ title }}
    </p>
    <p
      v-if="caption"
      class="beaver-medium mb-2 uppercase md:mb-3 md:text-center"
    >
      {{ caption }}
    </p>

    <div class="mb-6 flex items-center justify-center gap-8 md:mb-10">
      <AppLink :to="feedatyPageUrl" class="hidden md:block">
        <UtilsIcon name="Feedaty3.svg" class="h-32 w-32" />
      </AppLink>

      <div
        class="flex flex-col flex-wrap items-center gap-3"
        v-if="feedatyReviewsCount || feedatyRating"
      >
        <div class="flex w-fit flex-wrap items-center gap-3 md:justify-center">
          <div
            class="
              star-container
              bg-white
              text-yellow-main
              flex
              w-fit
              gap-3
              rounded-full
              px-5
              py-2
            "
          >
            <UtilsStars :size="5" :stars="feedatyRating" />
          </div>

          <p class="elephant-bold text-green-main">{{ feedatyRatingLabel }} su 5</p>
        </div>
        <p
          class="beaver text-black-main w-fit md:text-center"
          v-if="feedatyReviewsCount"
        >
          {{ $t("rails.reviews.total", { total: feedatyReviewsCountLabel }) }}
        </p>
      </div>
    </div>

    <DialogsAndModalsUtilsModal id="review">
      <DialogsAndModalsReview v-bind="reviewCards[activeIndex]" />
    </DialogsAndModalsUtilsModal>

    <AppCarousel
      v-if="reviewCards?.length"
      class="carousel-overflowed select-none"
      :swiper-props="carouselOptions"
      :items="reviewCards"
      :theme="carouselOptions?.theme"
      :key="sm ? 1 : 0"
    >
      <template #default="{ item, slideIndex }">
        <CardReview
          class="swiper-tile"
          v-bind="item"
          @on-read-more="handleReadMore(slideIndex)"
        />
      </template>
    </AppCarousel>
  </section>
</template>

<style lang="scss" scoped>

:deep(.swiper-slide:first-child > div) {
  margin-left: 0;
}

:deep(.swiper-slide:last-child > div) {
  margin-right: 0;
}
.swiper-tile {
  margin: 0 8px;
}

@screen md {
  .swiper-tile {
    margin: 0 12px;
    width: calc(((100vw - 142px) / 4) - 24px);
  }
}

@screen lg {
  .swiper-tile {
    width: calc(((100vw - 456px) / 4) - 24px);
  }
}

</style>
